/**
 * 头像部件库 —— 已全面迁移 PNG 精灵（gen/parts/ 下无损 WebP，1200×1200 = 200 网格 ×6）。
 *
 * 渲染层级（renderer 负责按此顺序拼装，PNG 路线）：
 *   背景 → 后发 → 脸(含耳+脖子) → 服装 → 胡须 → 岁月痕迹
 *   → 眉 → 眼(iris+frame) → 鼻 → 嘴 → 侧发 → 刘海 → 眼镜 → 配饰
 *
 * 染色规则（spriteTint）：
 *   skin = 脸型/鼻；hair = 各类头发/眉毛；服装原色直出不染（服色槽已删）；
 *   缺省 = 原色不染（眼框/嘴/将来的固定色配饰）；虹膜单独走 colors.iris（瞳色槽）。
 *
 * 部件元数据：
 *   gender: 'f' | 'm'  —— 性别归属（缺省=通用占位/色板类）；随机抽取与编辑器显示都按性别过滤
 *   age:    'mid' | 'old' —— 年龄部件（缺省=年轻可用）；随机 NPC（默认 young）永不抽到
 *
 * 待补 PNG 的类目（暂只有"无"占位）：胡须、岁月痕迹、配饰。
 */

export interface PartColors {
  skin: string;
  skinShade: string;
  hair: string;
  hairShade: string;
  line: string;
  /** 瞳色（PNG 虹膜模板染色） */
  iris?: string;
}

export interface Part {
  id: string;
  label: string;
  /** 性别倾向：随机抽取时过滤用；缺省 = 男女通用 */
  gender?: 'f' | 'm';
  /** 年龄部件标记：'mid'=中年起，'old'=老年；缺省 = 年轻可用 */
  age?: 'mid' | 'old';
  /** 随机权重（缺省=1） */
  weight?: number;
  /** 旧 SVG 接口保留：PNG 精灵件恒返回空串 */
  render: (c: PartColors) => string;
  /** PNG 部件精灵：去色无损 WebP 的 dataURL。存在时渲染走 <image> + 染色滤镜 */
  sprite?: string;
  /** 精灵染色取色：皮肤/头发色；缺省原色不染（原色服装、眼框等） */
  spriteTint?: 'skin' | 'hair';
  /** 眼睛双件：虹膜精灵（染瞳色），本件自身为眼框（原色）。仅 EYES 使用 */
  spriteIris?: string;
}

const stub = (): string => '';

/* ============================ 脸型（头 + 耳 + 完整脖子；女 5 款 + 男 1 款） ============================ */

export const FACES: Part[] = [
  { id: 'oval',    label: '鹅蛋脸', gender: 'f', spriteTint: 'skin', render: stub },
  { id: 'round',   label: '圆脸',   gender: 'f', spriteTint: 'skin', render: stub },
  { id: 'slim',    label: '短脸',   gender: 'f', spriteTint: 'skin', render: stub },
  { id: 'pointed', label: '瓜子脸', gender: 'f', spriteTint: 'skin', render: stub },
  { id: 'long',    label: '长脸',   gender: 'f', spriteTint: 'skin', render: stub },
  { id: 'mNormal', label: '普通脸', gender: 'm', spriteTint: 'skin', render: stub },
];

/* ============================ 后发（最底层，女 6 款） ============================ */

export const HAIR_BACKS: Part[] = [
  { id: 'sBob',       label: '波波头', gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sBun',       label: '盘头',   gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sLong',      label: '长直发', gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sPonytail',  label: '马尾',   gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sTwintail',  label: '双马尾', gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sLongWavy',  label: '长波浪', gender: 'f', spriteTint: 'hair', render: stub },
];

/* ============================ 侧发（女 3 款） ============================ */

export const HAIR_SIDES: Part[] = [
  { id: 'sSideNone',  label: '无刘海',   gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sSideBraid', label: '耳后编发', gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sSideLoose', label: '披发',     gender: 'f', spriteTint: 'hair', render: stub },
];

/* ============================ 前发（女 5 款） ============================ */

export const HAIR_FRONTS: Part[] = [
  { id: 'none',        label: '无',         gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sBangs',      label: '齐刘海',     gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sBangsWispy', label: '齐刘海碎须', gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sMiddlePart', label: '中分',       gender: 'f', spriteTint: 'hair', render: stub },
  { id: 'sChoppy',     label: '碎发刘海',   gender: 'f', spriteTint: 'hair', render: stub },
];

/* ============================ 头发（男，整头一件不拆前后侧） ============================ */

export const HAIRS: Part[] = [
  { id: 'mHairNormal',    label: '普通头', gender: 'm', spriteTint: 'hair', render: stub },
  { id: 'mHairSidepart',  label: '三七分', gender: 'm', spriteTint: 'hair', render: stub },
  { id: 'mSlickback',     label: '背头',   gender: 'm', spriteTint: 'hair', render: stub },
];

/* ============================ 眉毛（女 3 款共用 + 男 1 款专属） ============================ */

export const BROWS: Part[] = [
  { id: 'sHeroic',   label: '英气眉', spriteTint: 'hair', render: stub },
  { id: 'sGrievous', label: '委屈眉', spriteTint: 'hair', render: stub },
  { id: 'sRaised',   label: '挑眉',   spriteTint: 'hair', render: stub },
  { id: 'mBrow1',    label: '粗平眉', gender: 'm', spriteTint: 'hair', render: stub },
];

/* ============================ 眼睛（frame 原色 + iris 染瞳色；女 4 款 + 男 3 款） ============================ */

export const EYES: Part[] = [
  { id: 'sUpturned', label: '上挑眼', gender: 'f', spriteTint: 'skin', render: stub, spriteIris: undefined as unknown as string },
  { id: 'sRound',    label: '圆眼',   gender: 'f', spriteTint: 'skin', render: stub, spriteIris: undefined as unknown as string },
  { id: 'sGentle',   label: '温柔眼', gender: 'f', spriteTint: 'skin', render: stub, spriteIris: undefined as unknown as string },
  { id: 'sDroopy',   label: '下垂眼', gender: 'f', spriteTint: 'skin', render: stub, spriteIris: undefined as unknown as string },
  { id: 'mEye1',     label: '眼睛1',  gender: 'm', spriteTint: 'skin', render: stub, spriteIris: undefined as unknown as string },
  { id: 'mEye2',     label: '眼睛2',  gender: 'm', spriteTint: 'skin', render: stub, spriteIris: undefined as unknown as string },
  { id: 'mEye3',     label: '眼睛3',  gender: 'm', spriteTint: 'skin', render: stub, spriteIris: undefined as unknown as string },
];

/* ============================ 鼻子（女 2 款 + 男 2 款） ============================ */

export const NOSES: Part[] = [
  { id: 'sSmall',   label: '小鼻',   gender: 'f', spriteTint: 'skin', render: stub },
  { id: 'sDefined', label: '立体鼻', gender: 'f', spriteTint: 'skin', render: stub },
  { id: 'mNose1',   label: '鼻子1',  gender: 'm', spriteTint: 'skin', render: stub },
  { id: 'mNose2',   label: '鼻子2',  gender: 'm', spriteTint: 'skin', render: stub },
];

/* ============================ 嘴巴（原色；女 4 款 + 男 4 款） ============================ */

export const MOUTHS: Part[] = [
  { id: 'sSmirk',        label: '嘴角微翘', gender: 'f', render: stub },
  { id: 'sSlightlyOpen', label: '嘴微张',   gender: 'f', render: stub },
  { id: 'sPressed',      label: '抿嘴',     gender: 'f', render: stub },
  { id: 'sSmile',        label: '笑',       gender: 'f', render: stub },
  { id: 'mMouth1',       label: '嘴巴1',    gender: 'm', render: stub },
  { id: 'mLaugh',        label: '大笑',     gender: 'm', render: stub },
  { id: 'mSmirk',        label: '嘴角微翘', gender: 'm', render: stub },
  { id: 'mSlightlyOpen', label: '嘴微张',   gender: 'm', render: stub },
];

/* ============================ 胡须（待补 PNG，暂只有"无"） ============================ */

export const BEARDS: Part[] = [
  { id: 'none', label: '无', render: stub },
];

/* ============================ 岁月痕迹（原色；女 1 款 + 占位） ============================ */

export const AGE_LINES: Part[] = [
  { id: 'none', label: '无', render: stub },
  { id: 'sWrinkles', label: '皱纹', gender: 'f', render: stub },
];

/* ============================ 服装（原色不染；女 10 款 + 男 5 款） ============================ */

export const CLOTHES: Part[] = [
  { id: 'sTshirt',        label: '白T',       gender: 'f', render: stub },
  { id: 'sHoodie',        label: '红帽衫',   gender: 'f', render: stub },
  { id: 'sSuit',          label: '西装',     gender: 'f', render: stub },
  { id: 'sJacket',        label: '驼色夹克', gender: 'f', render: stub },
  { id: 'sSequin',        label: '亮片舞裙', gender: 'f', render: stub },
  { id: 'sJacketSweater', label: '蓝黄夹克', gender: 'f', render: stub },
  { id: 'sBowShirt',      label: '蝴蝶结衬衫', gender: 'f', render: stub },
  { id: 'sGown',          label: '礼服',     gender: 'f', render: stub },
  { id: 'sHoodie2',       label: '卫衣',     gender: 'f', render: stub },
  { id: 'sShirtTie',      label: '衬衫领带', gender: 'f', render: stub },
  { id: 'mSweater',       label: '黑毛衣',   gender: 'm', render: stub },
  { id: 'mShirtTie',      label: '衬衫领带', gender: 'm', render: stub },
  { id: 'mSuit',          label: '西装',     gender: 'm', render: stub },
  { id: 'mHoodie',        label: '卫衣',     gender: 'm', render: stub },
  { id: 'mSportswear',    label: '运动服',   gender: 'm', render: stub },
];

/* ============================ 眼镜（原色；女 1 款 + 男 1 款） ============================ */

export const GLASSES: Part[] = [
  { id: 'none', label: '不戴', render: stub },
  { id: 'sGlassesSquare',  label: '方框眼镜', gender: 'f', render: stub },
  { id: 'mGlassesSquare',  label: '方框眼镜', gender: 'm', render: stub },
];

/* ============================ 配饰（待补 PNG，暂只有"无"） ============================ */

/* ============================ 配饰（原色；耳圈女专属） ============================ */

export const ACCESSORIES: Part[] = [
  { id: 'none', label: '无', render: stub },
  { id: 'sHoopEarring', label: '耳圈', gender: 'f', render: stub },
];
